import React, { useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "./components/Sidebar";
import DepartmentsPage from "./pages/DepartmentsPage";
import AttendancePage from "./pages/AttendancePage";
import Dashboard from "./pages/Dashbord";
import LeaveManagement from "./pages/LeaveManagement";
import CompanyEvents from "./pages/CompanyEvents";
import EmployeeBenefits from "./pages/EmployeeBenefits";
import SalaryPayrollManager from "./pages/SalaryPayrollManager";
import EmployeeTraining from "./pages/EmployeeTraining";
import LoginSignupPage from "./pages/LoginSignupPage";
import CreateAccountPage from "./pages/CreateAccountPage";
import CreateEmployeeAccount from "./pages/CreateEmployeeAccount";
import Structure from "./pages/Structure";
import Designation from "./pages/Designation";
import Company from "./pages/Company";
import Profilepage from "./pages/Profilepage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import HireEmployees from "./pages/HireEmployees";
import EmployeeDocsPage from "./pages/EmployeeDocsPage";
import ResignationsPage from "./pages/ResignationsPage";
import ResignationDetailsPage from "./pages/ResignationDetailsPage";
import SetPassword from "./pages/SetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { API_BASE_URL } from "./config/apiconfig";

const MobileHeader = () => {
    const { user, userType, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [companyName, setCompanyName] = useState("");

    const isAuthenticated = !!user && !!userType;
    const isAdmin = userType === "user";

    React.useEffect(() => {
        const fetchCompanyName = async () => {
            try {
                if (!isAuthenticated) {
                    setCompanyName("");
                    return;
                }
                const token = localStorage.getItem("token");
                const type = localStorage.getItem("userType");
                const res = await axios.get(`${API_BASE_URL}/api/auth/company-name`, {
                    headers: {
                        "user-id": token,
                        "user-type": type,
                        "Authorization": `Bearer ${token}`,
                    },
                });
                setCompanyName(res.data?.companyName || "");
            } catch (_) {
                setCompanyName("");
            }
        };
        fetchCompanyName();
    }, [isAuthenticated]);

    const navLinks = useMemo(() => {
        if (!isAuthenticated) return [];
        if (isAdmin) {
            return [
                { name: "Employees", path: "/create-employee" },
                { name: "Company", path: "/company" },
                { name: "Attendance", path: "/attendance" },
                { name: "Leave", path: "/leave" },
                { name: "Employee Benefits", path: "/benefits" },
                { name: "Salary Calculator", path: "/salary-manager" },
                { name: "Training", path: "/training" },
                { name: "Hire Employees", path: "/hire" },
                { name: "Resignations", path: "/resignations" },
                { name: "Talent Pool", path: "https://e2e.valuecraftminds.com", external: true },
            ];
        }

        return [
            { name: "Profile", path: "/profile" },
            { name: "Attendance", path: "/attendance" },
            { name: "Leave", path: "/leave" },
            { name: "Training", path: "/training" },
            { name: "Talent Pool", path: "https://e2e.valuecraftminds.com", external: true },
        ];
    }, [isAuthenticated, isAdmin]);

    const closeMenu = () => setOpen(false);

    const handleLogout = async () => {
        try {
            await logout();
        } finally {
            closeMenu();
            navigate("/");
        }
    };

    return (
        <div className="lg:hidden sticky top-0 z-50 bg-gradient-to-b from-gray-900 to-black text-white border-b border-gray-800">
            <div className="h-14 px-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate(isAuthenticated ? "/dashboard" : "/")}
                    className="font-extrabold tracking-wide text-blue-400"
                >
                    E2E HRMS
                </button>

                <div className="flex justify-end">
                    {isAuthenticated ? (
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="h-10 w-10 flex items-center justify-center"
                            aria-label="Open menu"
                        >
                            <span className="block">
                                <span className="block w-6 h-0.5 bg-white mb-1"></span>
                                <span className="block w-6 h-0.5 bg-white mb-1"></span>
                                <span className="block w-6 h-0.5 bg-white"></span>
                            </span>
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md"
                        >
                            Login
                        </button>
                    )}
                </div>
            </div>

            {isAuthenticated ? (
                <div className="px-4 pb-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">
                                {companyName ? companyName : ""}
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="text-sm font-semibold">
                                {user?.name || user?.fullName || "User"}
                            </div>
                            <div className="text-xs text-gray-300">
                                {isAdmin ? "Admin" : (user?.designation || "Employee")}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {open && (
                <div className="fixed inset-0 z-50">
                    <button
                        type="button"
                        onClick={closeMenu}
                        className="absolute inset-0 bg-black/60"
                        aria-label="Close menu"
                    />

                    <div className="absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-gray-900 text-white shadow-2xl p-4 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="font-bold">{user?.name || user?.fullName || "User"}</div>
                                <div className="text-xs text-gray-300">{isAdmin ? "Admin" : (user?.designation || "Employee")}</div>
                            </div>
                            <button type="button" onClick={closeMenu} className="px-3 py-2 text-sm">Close</button>
                        </div>

                        <nav className="space-y-2">
                            {navLinks.map((l) =>
                                l.external ? (
                                    <a
                                        key={l.path}
                                        href={l.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={closeMenu}
                                        className="block px-4 py-3 rounded-md bg-gray-800 hover:bg-blue-600 transition-colors font-semibold"
                                    >
                                        {l.name}
                                    </a>
                                ) : (
                                    <Link
                                        key={l.path}
                                        to={l.path}
                                        onClick={closeMenu}
                                        className="block px-4 py-3 rounded-md bg-gray-800 hover:bg-blue-600 transition-colors font-semibold"
                                    >
                                        {l.name}
                                    </Link>
                                )
                            )}
                        </nav>

                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 rounded-md font-semibold"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AppRoutes = () => (
    <Routes>
        <Route path="/departments" element={<DepartmentsPage />} />
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/leave" element={<LeaveManagement />} />
        <Route path="/event" element={<CompanyEvents />} />
        <Route path="/benefits" element={<EmployeeBenefits />} />
        <Route path="/salary-manager" element={<SalaryPayrollManager />} />
        <Route path="/training" element={<EmployeeTraining />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/" element={<LoginSignupPage />} />
        <Route path="/create-account" element={<CreateAccountPage />} />
        <Route path="/create-employee" element={<CreateEmployeeAccount />} />
        <Route path="/profile" element={<Profilepage />} />
        <Route path="/structure" element={<Structure />} />
        <Route path="/designation" element={<Designation />} />
        <Route path="/company" element={<Company />} />
        <Route path="/announcement" element={<AnnouncementsPage />} />
        <Route path="/hire" element={<HireEmployees />} />
        <Route path="/employee-docs/:empNo" element={<EmployeeDocsPage />} />
        <Route path="/resignations" element={<ResignationsPage />} />
        <Route path="/resignations/:empNo" element={<ResignationDetailsPage />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
    </Routes>
);

const App = () => (
    <AuthProvider>
        <Router>
            <div className="flex h-screen">
                <div className="hidden lg:block">
                    <Sidebar />
                </div>
                <div className="flex-1 min-w-0 min-h-0 w-full overflow-y-auto overflow-x-hidden">
                    <MobileHeader />
                    <AppRoutes />
                </div>
            </div>
        </Router>
    </AuthProvider>
);

export default App;

